/**
 * Landing Page - Homepage
 * Hero, features, steps, target audience, CTA
 */

import { renderNavbar } from '../components/navbar.js';
import { renderFooter } from '../components/footer.js';

export function renderLanding(container) {
  container.innerHTML = `
    ${renderNavbar()}
    
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg"></div>
      <div class="container hero-content">
        <div class="hero-text animate-slideUp">
          <span class="hero-badge">
            <span>🏦</span>
            <span>Tu asesor hipotecario digital</span>
          </span>
          <h1 class="hero-title">
            Descubre si puedes <span class="text-gradient">comprar tu vivienda</span> y cómo hacerlo
          </h1>
          <p class="hero-subtitle">
            Analizamos tu perfil financiero y te guiamos paso a paso en todo el proceso: 
            desde calcular tu capacidad de ahorro hasta encontrar las mejores condiciones hipotecarias.
          </p>
          <div class="hero-cta">
            <a href="/auth" data-link class="btn btn-primary btn-lg">
              <i data-feather="play-circle"></i>
              Empieza gratis
            </a>
            <a href="#como-funciona" class="btn btn-secondary btn-lg">
              <i data-feather="info"></i>
              Cómo funciona
            </a>
          </div>
        </div>
        <div class="hero-visual animate-fadeIn">
          <div class="keys-3d-container">
            <div class="keys-3d-wrapper" id="keys3d">
              <div class="keys-3d-glow"></div>
              <div class="keys-3d-glow-inner"></div>
              <img src="/keys-3d.png" alt="Llaves de casa 3D" class="keys-3d-image" />
              <div class="keys-3d-particles">
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
                <span class="keys-3d-particle"></span>
              </div>
            </div>
          </div>
          <div class="hero-floating-card" style="top: 10%; left: -10%;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <div style="width: 40px; height: 40px; background: rgba(39, 174, 96, 0.15); border-radius: 8px; display: flex; align-items: center; justify-content: center;">
                <i data-feather="trending-up" style="color: #3cc28f; width: 20px;"></i>
              </div>
              <div>
                <div style="font-size: 12px; color: rgba(255,255,255,0.6);">Ahorro mensual</div>
                <div style="font-weight: 700; color: white;">+450€/mes</div>
              </div>
            </div>
          </div>
          <div class="hero-floating-card" style="bottom: 15%; right: -5%;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <div style="width: 40px; height: 40px; background: rgba(41, 128, 185, 0.15); border-radius: 8px; display: flex; align-items: center; justify-content: center;">
                <i data-feather="home" style="color: #4a7eb3; width: 20px;"></i>
              </div>
              <div>
                <div style="font-size: 12px; color: rgba(255,255,255,0.6);">Meta vivienda</div>
                <div style="font-weight: 700; color: white;">185.000€</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features" id="caracteristicas">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">Nuestros servicios</span>
          <h2 class="section-title">Todo lo que necesitas en un solo lugar</h2>
          <p class="section-subtitle">
            Herramientas profesionales diseñadas para el mercado inmobiliario español
          </p>
        </div>
        
        <div class="features-grid">
          <div class="card feature-card animate-slideUp stagger-1">
            <div class="feature-icon">
              <i data-feather="compass"></i>
            </div>
            <h3 class="feature-title">Guía paso a paso</h3>
            <p class="feature-description">
              Te acompañamos desde el principio hasta la firma. 
              Cada paso explicado de forma clara y sencilla.
            </p>
          </div>
          
          <div class="card feature-card animate-slideUp stagger-2">
            <div class="feature-icon">
              <i data-feather="sliders"></i>
            </div>
            <h3 class="feature-title">Calculadoras inteligentes</h3>
            <p class="feature-description">
              Conoce tu capacidad de ahorro, cuánto puedes pagar de hipoteca 
              y cuándo podrás comprar tu vivienda.
            </p>
          </div>
          
          <div class="card feature-card animate-slideUp stagger-3">
            <div class="feature-icon">
              <i data-feather="gift"></i>
            </div>
            <h3 class="feature-title">Ayudas públicas</h3>
            <p class="feature-description">
              Descubre todas las ayudas estatales y autonómicas 
              disponibles para tu situación específica.
            </p>
          </div>
          
          <div class="card feature-card animate-slideUp stagger-4">
            <div class="feature-icon">
              <i data-feather="target"></i>
            </div>
            <h3 class="feature-title">Plan de ahorro</h3>
            <p class="feature-description">
              Estrategias personalizadas para acelerar tu ahorro 
              y llegar antes a tu objetivo.
            </p>
          </div>
          
          <div class="card feature-card animate-slideUp stagger-5">
            <div class="feature-icon">
              <i data-feather="shield"></i>
            </div>
            <h3 class="feature-title">Análisis de mercado</h3>
            <p class="feature-description">
              Información actualizada según la normativa 
              y condiciones del mercado hipotecario vigente.
            </p>
          </div>
          
          <div class="card feature-card animate-slideUp stagger-5">
            <div class="feature-icon">
              <i data-feather="users"></i>
            </div>
            <h3 class="feature-title">Para todas las edades</h3>
            <p class="feature-description">
              Contenido adaptado para jóvenes, familias y mayores 
              con sus necesidades específicas.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- How it works Section -->
    <section class="steps" id="como-funciona">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">Cómo funciona</span>
          <h2 class="section-title">3 pasos para conseguir tu vivienda</h2>
          <p class="section-subtitle">
            Un proceso simple y guiado para que no te pierdas nada
          </p>
        </div>
        
        <div class="steps-list">
          <div class="step-item animate-slideIn stagger-1">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3 class="step-title">Conoce tu situación</h3>
              <p class="step-description">
                Completa tu perfil financiero y descubre cuánto puedes ahorrar, 
                qué hipoteca puedes permitirte y qué vivienda está a tu alcance.
              </p>
            </div>
          </div>
          
          <div class="step-item animate-slideIn stagger-2">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3 class="step-title">Sigue la guía hipotecaria</h3>
              <p class="step-description">
                Aprende todo lo que necesitas saber sobre hipotecas: tipos, gastos, 
                documentación y cómo negociar con los bancos.
              </p>
            </div>
          </div>
          
          <div class="step-item animate-slideIn stagger-3">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3 class="step-title">Consigue ayudas y ahorra</h3>
              <p class="step-description">
                Descubre las ayudas públicas a las que tienes derecho y optimiza 
                tu ahorro con nuestros consejos personalizados.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Target Audience Section -->
    <section class="audience" id="publico">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">¿Para quién es?</span>
          <h2 class="section-title">Contenido para cada etapa de tu vida</h2>
          <p class="section-subtitle">
            Información adaptada a tu situación personal y financiera
          </p>
        </div>
        
        <div class="audience-grid">
          <div class="audience-card animate-slideUp stagger-1">
            <div class="audience-emoji">👨‍🎓</div>
            <h3 class="audience-title">Jóvenes (18-35 años)</h3>
            <p class="audience-description">
              Primera vivienda, ahorro desde cero y ayudas específicas para menores de 35.
            </p>
            <ul class="audience-features">
              <li>Ayudas al alquiler y compra para jóvenes</li>
              <li>Estrategias de ahorro desde 0</li>
              <li>Aval ICO para primera vivienda</li>
              <li>Hipotecas para jóvenes</li>
            </ul>
          </div>
          
          <div class="audience-card animate-slideUp stagger-2">
            <div class="audience-emoji">👨‍👩‍👧‍👦</div>
            <h3 class="audience-title">Familias (35-55 años)</h3>
            <p class="audience-description">
              Cambio de vivienda, ampliación de espacio o mejora de condiciones hipotecarias.
            </p>
            <ul class="audience-features">
              <li>Mejora de hipoteca actual</li>
              <li>Deducciones fiscales por hijos</li>
              <li>Compra de vivienda más grande</li>
              <li>Subrogación y novación</li>
            </ul>
          </div>
          
          <div class="audience-card animate-slideUp stagger-3">
            <div class="audience-emoji">👴</div>
            <h3 class="audience-title">Mayores (+55 años)</h3>
            <p class="audience-description">
              Planificación de jubilación, hipoteca inversa y adaptación de vivienda.
            </p>
            <ul class="audience-features">
              <li>Hipoteca inversa</li>
              <li>Cancelación de hipoteca</li>
              <li>Ayudas para rehabilitación</li>
              <li>Nuda propiedad y usufructo</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2 class="cta-title">¿Listo para dar el primer paso?</h2>
        <p class="cta-subtitle">
          Toda la información que necesitas, totalmente gratis. Sin pagos, sin suscripciones.
        </p>
        <a href="/auth" data-link class="btn cta-button btn-lg">
          <i data-feather="arrow-right"></i>
          Crear cuenta gratis
        </a>
      </div>
    </section>

    ${renderFooter()}
  `;

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // 3D Keys Interactive Animation
  const keys3d = document.getElementById('keys3d');
  if (keys3d) {
    let isDragging = false;
    let startX, startY;
    let currentRotationY = 0;
    let currentRotationX = 5;
    let autoRotate = true;

    // AbortController to clean up global listeners on route change
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Clean up listeners when navigating away
    document.addEventListener('routeChanged', () => {
      abortController.abort();
    }, { once: true });

    // Mouse move - tilt effect (when not dragging)
    const keysContainer = keys3d.closest('.keys-3d-container');
    if (keysContainer) {
      keysContainer.addEventListener('mousemove', (e) => {
        if (isDragging) return;

        const rect = keysContainer.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;

        // Subtle tilt based on mouse position
        const tiltX = y * 15;
        const tiltY = -x * 15;

        keys3d.style.animation = 'none';
        keys3d.style.transform = `rotateY(${currentRotationY + tiltY}deg) rotateX(${currentRotationX + tiltX}deg)`;
      }, { signal });

      keysContainer.addEventListener('mouseleave', () => {
        if (!isDragging && autoRotate) {
          keys3d.style.animation = '';
        }
      }, { signal });
    }

    // Drag to rotate
    keys3d.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      keys3d.style.animation = 'none';
      e.preventDefault();
    }, { signal });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      currentRotationY += deltaX * 0.5;
      currentRotationX += deltaY * 0.3;

      // Clamp X rotation
      currentRotationX = Math.max(-30, Math.min(30, currentRotationX));

      keys3d.style.transform = `rotateY(${currentRotationY}deg) rotateX(${currentRotationX}deg)`;

      startX = e.clientX;
      startY = e.clientY;
    }, { signal });

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        // Resume auto-rotation after a delay
        setTimeout(() => {
          if (autoRotate) {
            keys3d.style.animation = '';
          }
        }, 2000);
      }
    }, { signal });
  }
}
